import { TENANTS_SLUGS } from 'constants/tenant-constants';
import AccountActivation from 'pages/auth-user/accountActivation';
import { UserCheckoutFailure, UserCheckoutSuccess, UserVerifyEmail } from 'pages/user-dashboard';
import { Fragment, lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { selectCurrentTenant } from 'reducers/selectors';
import { getBaseRedirectUrl, redirectUrlBasedUserType } from 'utils/tenant-util';
import AuthRoute from '../components/auth-route/AuthRoute';
import ProtectedRoute from '../components/protected-route/ProtectedRoute';
import { BASE_PATH, SIGNIN_PATH, UP_PATH } from '../constants/admin-paths';
import {
  USER_ACCOUNT_ACTIVATION_PATH,
  USER_BASE_PATH,
  USER_CHECKOUT_CANCEL_PATH,
  USER_CHECKOUT_SUCCESS_PATH,
  USER_FORGOT_PASSWORD_PATH,
  USER_INTAKE_PATH,
  USER_SIGNIN_PATH,
  USER_SIGNUP_PATH,
  USER_VERIFY_EMAIL_PATH,
  USER_VOUCHER_INTAKE_PATH,
} from '../constants/user-paths';
import NotFound from '../pages/404/not_found';
import ForgotPassword from '../pages/auth-user/forgotpassword';
import SigninUser from '../pages/auth-user/signin';
import SignupUser from '../pages/auth-user/signup';
import Signin from '../pages/auth/signin';
import LandingPage from '../pages/landing-page';
import TestRoute from '../pages/test-route/TestRoute';
import useOneSignal from '../services/push-notifications/PushNotifications';
import { currentBaseRoute, userType } from '../utils';
import { getCookie } from '../utils/cookies-util';
import { IntakeForm } from 'pages/external';
const ElevateAdminRoutes = lazy(() => import('./ElevateAdminRoutes'));
const ElevateUserRoutes = lazy(() => import('./ElevateUserRoutes'));

const BaseRoutes = ({ user = false, handleLogin = () => {} }) => {
  const { tenantData } = useSelector(selectCurrentTenant);
  const validToken = userType() === 'user' ? getCookie('access-token-user') : getCookie('access-token');
  const isBasePath = [BASE_PATH, USER_BASE_PATH]?.includes(currentBaseRoute());
  const isLoggedIn = user && validToken;
  const origin = window.location.origin;
  const isShowLandingPage = tenantData?.slug !== TENANTS_SLUGS.ORALONLY;

  useOneSignal();

  const ExternalRedirect = ({ target }) => {
    window.location.replace(target);
    return null;
  };

  useEffect(() => {
    redirectUrlBasedUserType({ tenantData });

    if (userType() === 'user') {
      document.title = `Patient -  ${tenantData?.name}`;
    } else if (userType() === 'admin') {
      document.title = `Admin -  ${tenantData?.name}`;
    }
  }, [origin, tenantData?.current_domain_type?.slug]);

  const testRoutes = [
    {
      path: USER_CHECKOUT_SUCCESS_PATH,
      element: <UserCheckoutSuccess fullPage={true} />,
      show: !isLoggedIn,
    },
    { path: USER_CHECKOUT_CANCEL_PATH, element: <UserCheckoutFailure fullPage />, show: !isLoggedIn },
    { path: USER_VERIFY_EMAIL_PATH, element: <UserVerifyEmail fullPage />, show: !isLoggedIn },
    { path: USER_INTAKE_PATH, element: <IntakeForm />, show: true },
    { path: USER_VOUCHER_INTAKE_PATH, element: <IntakeForm />, show: true },
  ];

  return (
    <Suspense fallback={<></>}>
      <Routes>
        {testRoutes?.map(({ path, element, show }, index) => {
          return show ? <Route key={index} path={path} element={element} /> : null;
        })}
        <Route path="/" element={<ExternalRedirect target={getBaseRedirectUrl({ tenantData })} />} />
        <Route path={UP_PATH} element={<TestRoute />} />
        {isShowLandingPage && <Route path="/lpv1" element={<LandingPage />} />}
        {isBasePath ? (
          <Fragment>
            {getBaseRedirectUrl({ tenantData }) === BASE_PATH && (
              <Route
                path={`${BASE_PATH}/*`}
                element={
                  <Routes>
                    <Route
                      path={SIGNIN_PATH?.replace(BASE_PATH, '')}
                      element={isLoggedIn ? <Navigate to={BASE_PATH} /> : <Signin handleLogin={handleLogin} />}
                    />
                    <Route
                      path={`/*`}
                      element={
                        <ProtectedRoute user={user}>
                          <ElevateAdminRoutes user={user} />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                }
              />
            )}
            <Route
              path={`${USER_BASE_PATH}/*`}
              element={
                <Routes>
                  <Route
                    path={USER_SIGNIN_PATH?.replace(USER_BASE_PATH, '')}
                    element={
                      <AuthRoute>
                        <SigninUser handleLogin={handleLogin} />
                      </AuthRoute>
                    }
                  />
                  <Route
                    path={USER_SIGNUP_PATH?.replace(USER_BASE_PATH, '')}
                    element={
                      <AuthRoute>
                        <SignupUser handleLogin={handleLogin} />
                      </AuthRoute>
                    }
                  />
                  <Route
                    path={USER_ACCOUNT_ACTIVATION_PATH?.replace(USER_BASE_PATH, '')}
                    element={
                      <AuthRoute>
                        <AccountActivation handleLogin={handleLogin} />
                      </AuthRoute>
                    }
                  />
                  <Route
                    path={USER_FORGOT_PASSWORD_PATH?.replace(USER_BASE_PATH, '')}
                    element={
                      <AuthRoute>
                        <ForgotPassword />
                      </AuthRoute>
                    }
                  />
                  <Route
                    path={`/*`}
                    element={
                      <ProtectedRoute user={user}>
                        <ElevateUserRoutes user={user} />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              }
            />
          </Fragment>
        ) : (
          <Route path="*" element={<NotFound />} />
        )}
      </Routes>
    </Suspense>
  );
};

export default BaseRoutes;
